import { DataMapApi } from '@/api/dataMapApi';
import { DataSourceTableType } from '@/types/dataMap';
import LogicFlow from '@logicflow/core';
import { M } from 'node_modules/vite/dist/node/types.d-aGj9QkWt';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { verticalLayoutNodes, calculateOntologyPosition } from '../utils/layoutHelper';

export interface DataSourceType {
  id: string;
  name: string;
  tables: DataSourceTableType[];
  createdTime?: string;
  databaseType?: string;
  datasourceType?: string;
  host?: string;
  initDatabase?: string;
  port?: number;
  updatedTime?: string;
  username?: string;
}

export const useInitDataMap = () => {
  const [mapViewsId, setMapViewsId] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const ontologyIdObjectid = searchParams.get('id'); // 本体对象Id
  const ontologyId = searchParams.get('ontologyId') ?? undefined; // 本体Id

  const [systems, setSystems] = useState<DataSourceType[]>([]);
  const [graphData, setGraphData] = useState<LogicFlow.GraphConfigData>({
    nodes: [],
    edges: [],
  });
  // 新增：创建映射视图的锁，防止并行调用
  const creatingMappingRef = useRef(false);
  // 新增：重试次数限制
  const createMappingRetryRef = useRef(0);
  // 新增：防抖定时器
  const createMappingTimerRef = useRef<NodeJS.Timeout | null>(null);  

  // 获取数据映射详情，无数据时自动创建映射视图
  const fetchInitialData = async (id: string) => {
    try {
      const detailRes = await DataMapApi.getObjectMappingDetail(id);
      console.log('数据映射详情:', detailRes.data);
      if (detailRes?.data) {
        let nodesList: LogicFlow.NodeConfig[] = [],
          edgesList: LogicFlow.EdgeConfig[] = [];
        // 处理节点展示
        if (detailRes?.data?.sources) {
          // 先收集所有数据源节点（暂不设置位置）
          detailRes?.data?.sources.forEach((e: any) => {
            const nodeData: LogicFlow.NodeConfig = {
              id: e.id,
              type: 'sql-node',
              x: e.x || 0, // 临时坐标，后续会重新计算
              y: e.y || 0,
              properties: {
                tableName: e.tableMetadata.tableName,
                id: e.tableMetadata.id,
                fields: e.tableMetadata.columns?.length
                  ? e.tableMetadata.columns.map((col: any) => ({
                      key: col.columnName,
                      type: col.dataType,
                      comment: (col as any).comment,
                      id: col.id,
                    }))
                  : [],
              },
            };
            nodesList.push(nodeData);
          });
          
          // 使用垂直单列布局，所有数据源节点在左侧垂直排列
          // 只对没有保存位置的节点进行自动布局
          const needsAutoLayout = nodesList.every(node => !node.x || !node.y || (node.x === 0 && node.y === 0));
          if (needsAutoLayout) {
            nodesList = verticalLayoutNodes(nodesList, {
              nodeWidth: 200,        // 与 sqlNode.ts 中的实际宽度一致
              fieldHeight: 24,       // 与 sqlNode.ts 中的字段高度一致
              nodeHeaderHeight: 60,  // 与 sqlNode.ts 中的基础高度一致
              startX: 100,           // 左侧起始X坐标
              startY: 100,           // 起始Y坐标
              verticalGap: 40,       // 节点间垂直间距（基于实际高度自动累加）
            });
          }
          
          setMapViewsId(detailRes.data.id?.toString() || '');
        }
        // 处理边节点展示
        if (detailRes?.data?.fields?.length) {
          // 判断当前边界点的起始锚点是否存在, 不存在则不添加边,避免脏数据导致渲染失败
          detailRes?.data?.fields.forEach((e: any) => {
            const itExist = detailRes?.data?.sources?.find(
              (sources: any) => sources.id === e.sourceId
            );
            if (itExist) {
              edgesList.push({
                id: e.id,
                sourceAnchorId: `${e.sourceId}_${e.columnName}_right`,
                sourceNodeId: `${e.sourceId}`,
                targetAnchorId: `ontology_${detailRes?.data.ontologyObjectId}_${e.attributeName}_left`,
                targetNodeId: `ontology_${detailRes?.data.ontologyObjectId}`,
                type: 'sql-edge-bezier',
                properties: {
                  isBackendEdge: true, // 标记为后端加载的边线
                },
              });
            }
          });
        }
        // 处理join边节点展示
        if (!!detailRes?.data?.joins?.length) {
          detailRes?.data?.joins.forEach((e: any) => {
            // 判断当前边界点的起始锚点是否存在, 不存在则不添加边,避免脏数据导致渲染失败
            const itExist = detailRes?.data?.sources.find(
              (sources: any) => sources.id === e.leftTableId
            );
            if (!!itExist) {
              edgesList.push({
                id: String(e.id),
                sourceAnchorId: `${e.leftTableId}_table_right`,
                sourceNodeId: `${e.leftTableId}`,
                targetAnchorId: `${e?.rightTableId}_table_left`,
                targetNodeId: `${e.rightTableId}`,
                type: 'sql-edge-bezier',
                properties: {
                  isBackendEdge: true, // 标记为后端加载的边线
                  stroke: '#333',
                  strokeWidth: 2,
                  conditions: e.conditions,
                },
              });
            }
          });
          console.log('edgesList', edgesList);
        }
        await getSettingsMappingObjectsList(nodesList, edgesList, ontologyId);
      } else {
        // 无数据时才创建映射视图，避免重复调用
        await creatMappings(id);
      }
    } catch (error) {
      console.error('获取数据映射详情失败:', error);
      await createMappingWithDebounce(id)
    }
  };
  // 新增：带防抖+锁+重试限制的创建映射视图方法
  const createMappingWithDebounce = async (id: string) => {
    // 1. 清除之前的防抖定时器
    if (createMappingTimerRef.current) {
      clearTimeout(createMappingTimerRef.current);
    }

    // 2. 设置防抖（500ms内重复触发则重新计时）
    createMappingTimerRef.current = setTimeout(async () => {
      // 3. 检查是否正在创建/重试次数超限
      if (creatingMappingRef.current || createMappingRetryRef.current >= 1) {
        console.warn('创建映射视图请求已限流（正在执行/重试次数超限）');
        createMappingRetryRef.current = 0; // 重置重试次数
        return;
      }

      try {
        creatingMappingRef.current = true; // 加锁
        createMappingRetryRef.current += 1; // 增加重试次数
        await creatMappings(id);
      } finally {
        creatingMappingRef.current = false; // 解锁
      }
    }, 300); // 防抖时间可根据业务调整
  };

  // 创建映射视图
  const creatMappings = async (id: string) => {
    try {
      const res = await DataMapApi.creatObjectMappingsViews(id);
      console.log('创建映射视图:', res);
      if (res.data) {
        setMapViewsId(String(res.data));
        if (ontologyIdObjectid) fetchInitialData(ontologyIdObjectid);
      }
    } catch (error) {
      console.error('创建映射视图失败:', error);
    }
  };

  // 获取数据源列表
  const getDataSources = async () => {
    try {
      const res = await DataMapApi.getDataSourcesList();
      let dataSources = res?.data?.length ? [...res.data] : [];
      
      // 固定添加本地节点数据源
      // const localNodeDataSource = {
      //   id: 'local-node',
      //   name: '本地节点',
      //   tables: [],
      //   datasourceType: 'LOCAL'
      // };
      
      // // 检查本地节点数据源是否已存在，不存在则添加
      // const localNodeExists = dataSources.some(source => source.id === 'local-node');
      // if (!localNodeExists) {
      //   dataSources.unshift(localNodeDataSource);
      // }
      
      setSystems(dataSources);
      console.log('获取数据源列表:', res);
    } catch (error) {
      console.error('获取数据源列表失败:', error);
    }
  };

  // 获取设置映射对象列表
  const getSettingsMappingObjectsList = async (
    nodesList: LogicFlow.NodeConfig[],
    edgesList: LogicFlow.EdgeConfig[],
    ontologyId?: string
  ) => {
    try {
      if (!ontologyId) return;
      const res = await DataMapApi.getSettingsMappingObjectsList(ontologyId);
      if (res.data.length) {
        const data = res.data.find(
          (e: any) => String(e.id) === String(ontologyIdObjectid)
        );
        console.log('ontologyIdObjectid', ontologyIdObjectid, res.data, data);
        
        // 计算本体节点字段
        const ontologyFields = data?.attributes?.map((e: any) => ({
          key: e['name'],
          type: '',//e['fromObjectName'], // 本体字段类型暂时隐藏
          id: e['id'],
        }));
        if(!ontologyFields?.length) return; // 如果不存在本体节点，无需重复添加
        // 使用智能算法计算本体节点位置（居中于数据源节点右侧）
        const ontologyPosition = calculateOntologyPosition(
          nodesList,
          ontologyFields.length,
          {
            nodeWidth: 200,     // 与 sqlNode.ts 中的实际宽度一致
            fieldHeight: 24,    // 与 sqlNode.ts 中的字段高度一致
            nodeHeaderHeight: 60, // 与 sqlNode.ts 中的基础高度一致
            horizontalGap: 250, // 本体节点与数据源节点的水平间距
          }
        );
        
        nodesList.push({
          id: `ontology_${data.id}`, // 添加ontology_前缀，避免与数据表节点id冲突
          type: 'sql-node',
          x: ontologyPosition.x,
          y: ontologyPosition.y,
          properties: {
            tableName: data.name,
            ontologyId: data.id,
            isItOntology: true, // '标识是否本体对象',
            fields: ontologyFields,
          },
        });
        setGraphData({ nodes: nodesList, edges: edgesList });
      }
    } catch (error) {
      console.error('获取设置映射对象列表失败:', error);
    }
  };

  // 初始化数据源列表
  useEffect(() => {
    getDataSources();
  }, []);

  // 监听 params 变化，执行初始化逻辑
  useEffect(() => {
    // 确保异步函数按顺序执行，且避免重复调用
    const init = async () => {
      if (ontologyIdObjectid) {
        await fetchInitialData(ontologyIdObjectid); // 先获取详情，无数据再创建
      }
    };
    init();
  }, [ontologyIdObjectid, ontologyId]); // 补充 ontologyId 到依赖数组

  return {
    mapViewsId,
    graphData,
    systems,
    setSystems,
    getDataSources,
  };
};
